import { QueryClient, useQuery } from '@tanstack/react-query'
import { fetchAllItems, ListUnisDocument } from '@vendia/control-plane-api'
import { useRecoilValue } from 'recoil'

import { selectedRoleState } from '../state'
import useControlPlaneApi from './use-control-plane-api'

const QUERY_KEY = 'listUnis'
const FIVE_MINUTES = 300000

export type ListUnis = NonNullable<ReturnType<typeof useListUnis>['listUnisQuery']['data']>

export default function useListUnis({ isAuthenticated = true } = {}) {
  const client = useControlPlaneApi()
  const selectedRoleName = useRecoilValue(selectedRoleState).name
  const listUnisQuery = useQuery({
    queryKey: [QUERY_KEY, selectedRoleName],
    queryFn: () =>
      fetchAllItems({
        fetchPage: (nextToken) =>
          client.graphqlFetch({
            query: ListUnisDocument,
            variables: { limit: 50, nextToken },
          }),
        selectItems: (page) => page?.listUnis?.unis ?? [],
        getNextToken: (page) => page?.listUnis?.nextToken ?? null,
      }),
    staleTime: FIVE_MINUTES,
    // Allow user/roles to load and logic in App.tsx to initialize selectedRole before firing
    // off first request to listUnis. Ensures we're requesting the correct unis and prevents
    // glitchy situation where app appears to finish loading, then pops back into loading state
    // (also breaks Cypress tests which think they can proceed before app is done loading)
    enabled: isAuthenticated && !!selectedRoleName,
  })

  // Fix for dynamodb sentinel value (replaces empty string in db) leaking to frontend
  if (Array.isArray(listUnisQuery.data)) {
    listUnisQuery.data.forEach((uni) => {
      if (uni.alias === '\u0000' || uni.alias === '\x00') {
        uni.alias = null
      }
    })

    // Sort by uni.created (eg, "2025-02-25T03:03:35.209792+00:00" date)
    listUnisQuery.data.sort((a, b) => {
      // if created is null, sort to end of list
      if (!a.created && !b.created) {
        return 0
      }
      if (!a.created) {
        return 1
      }
      if (!b.created) {
        return -1
      }
      const dateA = new Date(a.created).getTime()
      const dateB = new Date(b.created).getTime()
      return dateB - dateA
    })
  }

  return {
    listUnisQuery,
  }
}

export function refetchUnis({ queryClient }: { queryClient: QueryClient }) {
  return queryClient.invalidateQueries({ queryKey: [QUERY_KEY], refetchType: 'inactive' })
}
